import type { SrsState } from '../types'

const DAY = 24 * 60 * 60 * 1000

export function freshSrs(): SrsState {
  return { interval: 0, ease: 2.5, due: null, reps: 0, lapses: 0 }
}

/** How the learner rated the recall, mapped from a drill score in `gradeToRating`. */
export type Rating = 'again' | 'hard' | 'good' | 'easy'

export function gradeToRating(score: number): Rating {
  if (score < 60) return 'again'
  if (score < 80) return 'hard'
  if (score < 95) return 'good'
  return 'easy'
}

/**
 * SM-2 with the usual practical tweaks: a lapse drops you to a one-day interval
 * rather than back to zero, and ease has a 1.3 floor so a run of bad days can't
 * make an island permanently punishing.
 */
export function schedule(prev: SrsState, rating: Rating, now = Date.now()): SrsState {
  const next: SrsState = { ...prev }

  if (rating === 'again') {
    next.lapses += 1
    next.reps = 0
    next.ease = Math.max(1.3, prev.ease - 0.2)
    next.interval = 1
  } else {
    next.reps = prev.reps + 1
    const delta = rating === 'hard' ? -0.15 : rating === 'easy' ? 0.15 : 0
    next.ease = Math.max(1.3, prev.ease + delta)

    if (next.reps === 1) next.interval = 1
    else if (next.reps === 2) next.interval = 3
    else next.interval = Math.round(prev.interval * next.ease * (rating === 'hard' ? 0.7 : 1))

    next.interval = Math.max(1, next.interval)
  }

  next.due = now + next.interval * DAY
  return next
}

export function isDue(srs: SrsState, now = Date.now()): boolean {
  return srs.due === null || srs.due <= now
}

/** Human-readable "due in" label for the island list. */
export function dueLabel(srs: SrsState, now = Date.now()): string {
  if (srs.due === null) return 'never drilled'
  const diff = srs.due - now
  if (diff <= 0) return 'due now'
  const days = Math.ceil(diff / DAY)
  if (days === 1) return 'due tomorrow'
  return `due in ${days} days`
}
